import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';

import InputField from '@/components/atoms/InputField';
import euroIcon from '@/assets/icons/euro.svg';

// Import the form schema from types
import { accountingCenterFormSchema, AccountingCenterFormValues } from '@/services/types/accounting-centers-types';
import TextAreaField from '@/components/atoms/TextAreaField';
import SliderInputField from '@/components/molecules/SliderInputField';
import SliderField from '@/components/atoms/SliderField';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface ACCreationFormProps {
  onSubmit?: (data: AccountingCenterFormValues) => void;
  className?: string;
  isSubmitting?: boolean;
  defaultValues?: Partial<AccountingCenterFormValues>;
  isEditMode?: boolean;
}

const ACCreationForm: React.FC<ACCreationFormProps> = ({
  onSubmit,
  className,
  isSubmitting = false,
  defaultValues,
  isEditMode = false
}) => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty }
  } = useForm<AccountingCenterFormValues>({
    resolver: zodResolver(accountingCenterFormSchema),
    defaultValues: defaultValues || {
      name: '',
      description: '',
      acmev: 0,
      acmev_alert_threshold: 0,
      acrt: 0,
      acrt_alert_threshold: 0
    }
  });

  // Reset form when defaultValues change
  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  const acmev = watch('acmev');
  const acrt = watch('acrt');
  const acmev_alert_threshold = watch('acmev_alert_threshold');
  const acrt_alert_threshold = watch('acrt_alert_threshold');

  // Debug: log errors if any
  if (Object.keys(errors).length > 0) {
    console.log('Form errors:', errors);
  }

  const onSubmitForm = (data: AccountingCenterFormValues) => {
    if (onSubmit) {
      onSubmit({
        ...data,
        acmev: Number(data.acmev),
        acrt: Number(data.acrt),
        acmev_alert_threshold: Number(data.acmev_alert_threshold),
        acrt_alert_threshold: Number(data.acrt_alert_threshold)
      });
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <>
      <form className={`flex-1 flex flex-col gap-4 ${className}`} onSubmit={handleSubmit(onSubmitForm)}>
        <div className="space-y-5 flex-1">
          <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
            <InputField
              className="flex-1 w-full"
              label="Accounting center name"
              placeholder="Enter accounting center name..."
              name="name"
              type="text"
              register={register}
              error={errors.name}
              disabled={isSubmitting}
              size="sm"
            />
            <span className="flex-1"></span>
          </div>

          <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
            <TextAreaField
              name="description"
              label="Budget Description"
              placeholder="Enter accounting center description..."
              register={register}
              className="w-full"
              error={errors.description}
              labelClassName="md:text-sm"
            />
          </div>

          <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
            <div className="flex-1 w-full">
              <SliderInputField
                name="acmev"
                label="Accounting center maximum expense value (ACM)"
                inputPlaceholder="e.g 100000"
                value={Number(acmev) || 0}
                onChange={(val) => setValue('acmev', val, { shouldDirty: true })}
                error={errors.acmev}
                min={0}
                maxValue={100000}
                icon={euroIcon}
                tooltipText="ACMEV cannot exceed BMEV (€100.000)"
              />

              <p className="text-sm text-secondary mt-[0.35rem]">ACMEV cannot exceed BMEV (€100.000)</p>
            </div>
            <div className="flex-1 w-full">
              <SliderInputField
                name="acrt"
                label="Accounting center revenue target (ACRT)"
                inputPlaceholder="e.g 500000"
                value={Number(acrt) || 0}
                onChange={(val) => setValue('acrt', val, { shouldDirty: true })}
                error={errors.acrt}
                min={0}
                maxValue={500000}
                icon={euroIcon}
                tooltipText="ACRT cannot exceed BTRT (€500.000)"
              />

              <p className="text-sm text-secondary mt-[0.35rem]">ACRT cannot exceed BTRT (€500.000)</p>
            </div>
          </div>

          <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
            <SliderField
              name="acmev_alert_threshold"
              label="Accounting center maximum expense value (ACM)"
              value={[Number(acmev_alert_threshold) || 0]}
              onValueChange={([value]) => setValue('acmev_alert_threshold', value, { shouldDirty: true })}
              className="flex-1"
              error={errors.acmev_alert_threshold}
            />

            <SliderField
              name="acrt_alert_threshold"
              label="Accounting center revenue target (ACRT)"
              value={[Number(acrt_alert_threshold) || 0]}
              onValueChange={([value]) => setValue('acrt_alert_threshold', value, { shouldDirty: true })}
              className="flex-1"
              error={errors.acrt_alert_threshold}
            />
          </div>
        </div>
        {/* FOOTER */}
        <footer className="py-2 bg-gradient-to-b from-[#F0EFFD]/0 from-0% to-[#F0EFFD] to-[17.74%] md:bg-white md:bg-none md:from-transparent md:to-transparent md:flex md:justify-end">
          <Button
            className="hidden md:block rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit text-[#E8507E]"
            variant={'text'}
            onClick={handleCancel}
            // disabled={isSubmitting}
            type="button"
          >
            Cancel
          </Button>
          <Button
            className="rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit"
            type="submit"
            disabled={!isDirty}
          >
            {isEditMode ? 'SAVE' : 'CREATE'}
          </Button>
        </footer>
      </form>
    </>
  );
};

export default ACCreationForm;
